import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { personasService } from '../services/personas.service';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';
import { AIPersona, AIPersonaPayload } from '../types/persona.type';

export function usePersonas() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['ai_personas', companyId],
    queryFn: () => personasService.getPersonas(companyId),
    enabled: !!companyId,
  });

  const createMutation = useMutation({
    mutationFn: (payload: AIPersonaPayload) => personasService.createPersona(companyId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai_personas', companyId] });
      toast.success('Đã tạo Đặc vụ mới thành công');
    },
    onError: (error: any) => {
      toast.error('Lỗi khi tạo Đặc vụ: ' + error.message);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<AIPersonaPayload> }) => 
      personasService.updatePersona(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai_personas', companyId] });
      toast.success('Đã cập nhật cấu hình Đặc vụ');
    },
    onError: (error: any) => {
      toast.error('Lỗi cập nhật: ' + error.message);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => personasService.deletePersona(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai_personas', companyId] });
      toast.success('Đã xóa Đặc vụ khỏi hệ thống');
    },
    onError: (error: any) => {
      toast.error('Lỗi khi xóa: ' + error.message);
    }
  });

  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, currentStatus }: { id: string; currentStatus: boolean }) => 
      personasService.toggleStatus(id, currentStatus),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['ai_personas', companyId] });
      toast.success(data.is_active ? 'Đã kích hoạt Đặc vụ' : 'Đã tạm dừng Đặc vụ');
    },
    onError: (error: any) => {
      toast.error('Lỗi khi đổi trạng thái: ' + error.message);
    }
  });

  const seedMutation = useMutation({
    mutationFn: () => personasService.seedDefaultPersonas(companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai_personas', companyId] });
      toast.success('Đã khôi phục Đội ngũ Đặc vụ AI mặc định');
    },
    onError: (error: any) => {
      toast.error('Lỗi khi khôi phục dữ liệu: ' + error.message);
    }
  });

  const resetMutation = useMutation({
    mutationFn: async (persona: AIPersona) => {
      const { DEFAULT_AGENT_PERSONAS } = await import('@/core/ai/ai.service');
      const defaultData = Object.values(DEFAULT_AGENT_PERSONAS).find(p => p.id.toUpperCase() === persona.code.toUpperCase());
      if (!defaultData) throw new Error('Đặc vụ này không có bản gốc để khôi phục');
      
      return personasService.updatePersona(persona.id, {
        name: defaultData.name,
        instruction: defaultData.instruction,
        icon: (defaultData as any).icon || 'ri-robot-line',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ai_personas', companyId] });
      toast.success('Đã khôi phục Đặc vụ về thiết lập gốc');
    },
    onError: (error: any) => {
      toast.error('Lỗi khi khôi phục: ' + error.message);
    }
  });

  return {
    personas: query.data || [],
    isLoading: query.isLoading,
    isError: query.isError,
    createPersona: createMutation.mutateAsync,
    updatePersona: updateMutation.mutateAsync,
    deletePersona: deleteMutation.mutateAsync,
    toggleStatus: toggleStatusMutation.mutateAsync,
    seedDefaultPersonas: seedMutation.mutateAsync,
    resetPersona: resetMutation.mutateAsync,
    isMutating: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending || toggleStatusMutation.isPending || seedMutation.isPending || resetMutation.isPending
  };
}
